import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {Divider, Grid} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import {ANTIBODY_REST_PROPS_BY_ID} from 'antibodies/AntibodyConstants';
import {useAntibodyFormatsQuery} from 'antibodies/hooks/useAntibodyFormatsQuery';
import AntibodyComponentSvg, {
  AntibodyComponentType,
} from 'antibodies/nested-data-creation/antibody-component-visualization/AntibodyComponentSvg';
import {
  ALLOWED_ANTIBODY_FORMAT_IDS,
  NestedDataType,
} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import {GetNDCSchemaConfig} from 'antibodies/nested-data-creation/panes/__generated/GetNDCSchemaConfig';
import {RegistryIds} from 'antibodies/nested-data-creation/panes/__generated/RegistryIds';
import AntibodyFormatPicker from 'antibodies/schema-configuration/AntibodyFormatPicker';
import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import RegistrySelector from 'registry/RegistrySelector';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

import {AntibodyFormatGraphQL} from '../../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';

interface InputOutputComponentButtonProps {
  componentType: AntibodyComponentType;
  componentName: string;
  isSelected: boolean;
}

function InputOutputComponentButton({
  componentType,
  componentName,
  isSelected,
}: InputOutputComponentButtonProps): JSX.Element {
  return (
    <div className={css(styles.buttonWrapper)}>
      <button
        className={classNames(css(styles.button), {
          [css(styles.selectedButton)]: isSelected,
        })}
        onClick={noop}
        type="button"
        aria-label={`${componentName} option`}
      >
        <AntibodyComponentSvg componentType={componentType} size={120} />
      </button>
      <div className={css(styles.buttonLabel)}>{componentName}</div>
    </div>
  );
}

export const REGISTRY_IDS_QUERY = gql`
  query RegistryIds @skipAudit @engComponent(name: REGISTRY) {
    registries {
      ... on RegistryNode {
        id
      }
    }
  }
`;

export const NDC_CONFIG_QUERY = gql`
  query GetNDCSchemaConfig($registryId: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    ndcSchemaConfig(registryId: $registryId) {
      ... on NDCSchemaConfigNode {
        aaDomainSchemaId
        dnaPairSchemaId
      }
    }
  }
`;

/**
 * Nested data creation modal step that allows the user to select the format of the antibodies they want to create.
 *
 * Current limitations:
 * - Only supports selecting from antibody formats that involve a single variable pair (VH/VL).
 * - The input and output components are not configurable by the user: the input is always DNA domain
 *   and both AA domains and AA chains must always be created as outputs.
 */
export interface SelectFormatPaneProps {
  selectedRegistryId: string | null;
  setSelectedRegistryIdAndDependentConfig: (
    registryId: string | null,
    pairSchemaId: string | null,
    constantDomainSchemaId: string | null
  ) => void;
  selectedFormatId: string | null;
  setSelectedFormat: (format: AntibodyFormatGraphQL | null) => void;
  startingDataType: NestedDataType;
  outputTypes: ReadonlySet<NestedDataType>;
}

export default function SelectFormatPane({
  selectedRegistryId,
  setSelectedRegistryIdAndDependentConfig,
  selectedFormatId,
  setSelectedFormat,
  startingDataType,
  outputTypes,
}: SelectFormatPaneProps): JSX.Element {
  const antibodyFormats = useAntibodyFormatsQuery();
  // After a registry is selected, we need to wait for the dependent config to be fetched before we can
  // set the selected registry and dependent config, so store it temporarily in local state.
  const [unprocessedSelectedRegistryId, setUnprocessedSelectedRegistryId] = useState<string | null>(
    selectedRegistryId
  );
  const [registryErrorMessage, setRegistryErrorMessage] = useState<string | null>(null);
  const registryIdQueryResult = useBenchlingQuery<RegistryIds>(REGISTRY_IDS_QUERY);
  const ndcConfigQueryResult = useSkippableBenchlingQuery<GetNDCSchemaConfig>(NDC_CONFIG_QUERY, {
    variables: {registryId: unprocessedSelectedRegistryId!},
    skip: unprocessedSelectedRegistryId == null,
  });
  // Extract the results for easier useEffect dependencies. We can't use the query results as a dependency
  // because useEffect does a shallow comparison, and the identity of the object changes continuously.
  const ndcConfigIsInaccessible =
    ndcConfigQueryResult.type === QueryResultType.SUCCESS &&
    isInaccessible(ndcConfigQueryResult.data.ndcSchemaConfig);
  const dnaPairSchemaId =
    ndcConfigQueryResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(ndcConfigQueryResult.data.ndcSchemaConfig)
      ? ndcConfigQueryResult.data.ndcSchemaConfig.dnaPairSchemaId
      : null;
  const aaDomainSchemaId =
    ndcConfigQueryResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(ndcConfigQueryResult.data.ndcSchemaConfig)
      ? ndcConfigQueryResult.data.ndcSchemaConfig.aaDomainSchemaId
      : null;

  useEffect(() => {
    // If there is only one registry, select it by default. RegistrySelector would do this itself, but we
    // don't render RegistrySelector if there is only one registry, so we need to do it here.
    if (
      registryIdQueryResult.type === QueryResultType.SUCCESS &&
      registryIdQueryResult.data.registries.length === 1 &&
      selectedRegistryId !== registryIdQueryResult.data.registries[0].id
    ) {
      setUnprocessedSelectedRegistryId(registryIdQueryResult.data.registries[0].id);
    }
  }, [registryIdQueryResult, selectedRegistryId]);

  useEffect(() => {
    if (ndcConfigQueryResult.type === QueryResultType.SUCCESS) {
      // If the registry was selected, it should be accessible.
      assert(!ndcConfigIsInaccessible, 'Expected registry to be accessible');
      // If the query is a success and the registry is accessible, missing schema ids mean they don't exist.
      const missingSchemas: Array<string> = [];
      if (dnaPairSchemaId == null) {
        missingSchemas.push('DNA pair');
      }
      if (aaDomainSchemaId == null) {
        missingSchemas.push('AA domain');
      }
      if (missingSchemas.length > 0) {
        setRegistryErrorMessage(
          `This registry is missing the ${missingSchemas.join(' and ')} ${pluralize(missingSchemas.length, 'schema', 'schemas')} necessary to create antibodies.`
        );
      } else {
        setRegistryErrorMessage(null);
      }
      setSelectedRegistryIdAndDependentConfig(
        unprocessedSelectedRegistryId,
        dnaPairSchemaId || null,
        aaDomainSchemaId || null
      );
    }
  }, [
    ndcConfigQueryResult.type,
    ndcConfigIsInaccessible,
    dnaPairSchemaId,
    aaDomainSchemaId,
    setSelectedRegistryIdAndDependentConfig,
    unprocessedSelectedRegistryId,
  ]);

  const antibodyProps = antibodyFormats
    .filter(({id}) => ALLOWED_ANTIBODY_FORMAT_IDS.includes(id))
    .map((format) => ({
      format,
      showLabels: false,
      ...ANTIBODY_REST_PROPS_BY_ID[format.id],
    }));

  function onSelectFormatId(formatId: string | null): void {
    const format = formatId != null ? antibodyFormats.find(({id}) => id === formatId) : null;
    setSelectedFormat(format ?? null);
  }

  const isLoading =
    registryIdQueryResult.type === QueryResultType.PENDING ||
    ndcConfigQueryResult.type === QueryResultType.PENDING;

  // Show the selector if there is more than one registry or if there is an error.
  const showRegistrySelector =
    (registryIdQueryResult.type === QueryResultType.SUCCESS &&
      registryIdQueryResult.data.registries.length !== 1) ||
    registryErrorMessage != null;

  return (
    <div>
      {isLoading ? (
        <HorizontalThrobber />
      ) : showRegistrySelector ? (
        <>
          <h3>Registry</h3>
          <RegistrySelector
            onChange={({target: {value: registry}}) => {
              setUnprocessedSelectedRegistryId(registry?.id ?? null);
            }}
            registryId={unprocessedSelectedRegistryId}
          />
          {registryErrorMessage != null && (
            <div className={css(styles.banner)}>
              <InlineBanner bannerType={InlineBannerType.ERROR} text={registryErrorMessage} />
            </div>
          )}
        </>
      ) : null}

      <h3>Format</h3>
      <p>Select the format of the antibodies you want to create</p>
      <AntibodyFormatPicker
        antibodyProps={antibodyProps}
        disabled={false}
        selectedFormat={selectedFormatId}
        setSelectedFormat={onSelectFormatId}
      />

      <Divider my="md" />

      <h3>Nested components</h3>
      <p>
        Select the input component and any additional output components you want to generate alongside the
        antibodies
      </p>

      <Grid>
        <Grid.Col span={6} {...getTestProps({component: 'SelectFormatPane', element: 'inputsSection'})}>
          <h5>INPUT COMPONENT</h5>
          <div className={css(styles.inputOutputSection)}>
            <InputOutputComponentButton
              componentType={AntibodyComponentType.DNA_DOMAIN}
              componentName="DNA domain"
              isSelected={startingDataType === NestedDataType.DNA_DOMAIN}
            />
            <InputOutputComponentButton
              componentType={AntibodyComponentType.AA_DOMAIN}
              componentName="AA domain"
              isSelected={startingDataType === NestedDataType.AA_DOMAIN}
            />
          </div>
        </Grid.Col>

        <Grid.Col span={6} {...getTestProps({component: 'SelectFormatPane', element: 'outputsSection'})}>
          <h5>OUTPUT COMPONENTS</h5>
          <div className={css(styles.inputOutputSection)}>
            <InputOutputComponentButton
              componentType={AntibodyComponentType.AA_DOMAIN}
              componentName="AA domain"
              isSelected={outputTypes.has(NestedDataType.AA_DOMAIN)}
            />
            <InputOutputComponentButton
              componentType={AntibodyComponentType.AA_CHAIN}
              componentName="AA chain"
              isSelected={outputTypes.has(NestedDataType.AA_CHAIN)}
            />
          </div>
        </Grid.Col>
      </Grid>
    </div>
  );
}

const styles = StyleSheet.create({
  buttonWrapper: {
    width: 'max-content',
  },
  button: {
    border: 'none',
    borderRadius: '4px',
    padding: 0,
  },
  selectedButton: {
    border: `1px solid ${Color.BLUE_450}`,
  },
  buttonLabel: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    display: 'flex',
    justifyContent: 'center',
    marginTop: '4px',
  },
  inputOutputSection: {
    display: 'flex',
    gap: '16px',
  },
  banner: {
    marginTop: '16px',
  },
});
